import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import { Form, Field } from 'components/forms';
import { BfButton, BfTextInput, BfSpinner } from 'components/Butterfly';
import Modal from 'src/components/project/analysis/results/Modal.vue';
import { ThemeGroup } from 'src/api/query';
import { mapGroupTreeToElTree } from './ThemesTree.vue';
import { findNode } from './ThemeBuilder.utils';
const reduceCheckedStructure = (nodes) => {
    const result = [];
    nodes.forEach((node) => {
        if (node.type === 'group' && node.children) {
            const allChildrenSelected = node.children.every((child) => child.checked);
            const processedChildren = reduceCheckedStructure(node.children);
            if (allChildrenSelected) {
                // Retain group if all children are selected
                result.push({ ...node, children: processedChildren });
            }
            else {
                // Remove group and move children up if partially selected
                result.push(...processedChildren.filter((child) => child.checked));
            }
        }
        else if (node.checked) {
            result.push(node);
        }
    });
    return result;
};
const mapStructureToChecked = (structure) => {
    return structure.map((node) => {
        const treeNode = {
            id: node.id,
            type: node.type,
            checked: false,
        };
        if (node.children) {
            treeNode.children = mapStructureToChecked(node.children);
        }
        return treeNode;
    });
};
const mapCheckedToSubmit = (structure) => {
    return structure.map((node) => {
        const treeNode = {
            id: node.id,
            type: node.type,
        };
        return treeNode;
    });
};
const CreateGroupModal = defineComponent({
    components: {
        Form,
        Field,
        BfButton,
        BfTextInput,
        Modal,
        BfSpinner,
    },
    props: {
        visible: { type: Boolean, required: true },
        submitErrors: { type: Array, required: true },
        analysisId: { type: Number, required: true },
        projectId: { type: Number, required: true },
    },
    setup(props, { emit }) {
        const form = ref();
        const isLoading = ref(false);
        const checkedStructure = ref([]);
        const groupStructure = ref([]);
        const errors = ref([]);
        const allErrors = computed(() => {
            return errors.value.concat(props.submitErrors);
        });
        const initialFormValues = computed(() => {
            return {
                name: '',
            };
        });
        const onCheckChange = (data, checked) => {
            const node = findNode(checkedStructure.value, { id: data.id, type: data.type });
            if (!node)
                return;
            node.checked = checked;
        };
        const submit = async (vals) => {
            const checkedChildren = reduceCheckedStructure(checkedStructure.value);
            const submitChildren = mapCheckedToSubmit(checkedChildren);
            const payload = {
                group_name: vals.name,
                children: submitChildren,
            };
            try {
                isLoading.value = true;
                await ThemeGroup.create(props.projectId, props.analysisId, payload);
            }
            catch (e) {
                const error = e;
                errors.value = error.body.non_field_errors;
            }
            finally {
                emit('update-tree');
                emit('close');
                isLoading.value = false;
            }
        };
        watch(() => props.visible, async () => {
            errors.value = [];
            isLoading.value = true;
            try {
                const { group_tree } = await ThemeGroup.list(props.projectId, props.analysisId);
                checkedStructure.value = mapStructureToChecked(group_tree);
                groupStructure.value = mapGroupTreeToElTree(group_tree);
            }
            catch {
                errors.value = ['Failed to load group list.'];
            }
            finally {
                isLoading.value = false;
            }
        }, {
            immediate: true,
        });
        return {
            onCheckChange,
            initialFormValues,
            form,
            submit,
            isLoading,
            groupStructure,
            allErrors,
        };
    },
});
export default CreateGroupModal;
